/**
 * Football system presets for the Vocabulary & Composition onboarding wizard.
 *
 * Each preset defines a set of vocabulary entries (labels only, no entity data)
 * that are imported into a playbook to bootstrap the coach's vocabulary.
 *
 * Category-to-entity-type mapping:
 * - formation -> formation
 * - modifier  -> modifier
 * - route, blocking, motion, protection -> concept
 */

import type { VocabularyCategory, VocabularyEntityType } from '@/db/Types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PresetVocabularyEntry {
	code: string
	category: VocabularyCategory
	entityType: VocabularyEntityType
	displayName: string
	description?: string
}

export interface PresetSystem {
	id: 'pro_style' | 'air_raid' | 'spread' | 'custom'
	name: string
	description: string
	vocabulary: PresetVocabularyEntry[]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formation(code: string, displayName: string, description?: string): PresetVocabularyEntry {
	return { code, category: 'formation', entityType: 'formation', displayName, description }
}

function modifier(code: string, displayName: string, description?: string): PresetVocabularyEntry {
	return { code, category: 'modifier', entityType: 'modifier', displayName, description }
}

function route(code: string, displayName: string, description?: string): PresetVocabularyEntry {
	return { code, category: 'route', entityType: 'concept', displayName, description }
}

function blocking(code: string, displayName: string, description?: string): PresetVocabularyEntry {
	return { code, category: 'blocking', entityType: 'concept', displayName, description }
}

function motion(code: string, displayName: string, description?: string): PresetVocabularyEntry {
	return { code, category: 'motion', entityType: 'concept', displayName, description }
}

function protection(code: string, displayName: string, description?: string): PresetVocabularyEntry {
	return { code, category: 'protection', entityType: 'concept', displayName, description }
}

// ---------------------------------------------------------------------------
// Pro Style — 46 entries
// ---------------------------------------------------------------------------

const PRO_STYLE_VOCABULARY: PresetVocabularyEntry[] = [
	// Formations (7)
	formation('Pro', 'Pro', 'Two backs, one tight end, two wide receivers'),
	formation('I', 'I Formation', 'Fullback and tailback aligned behind quarterback'),
	formation('Strong', 'Strong', 'Tight end and flanker on the same side'),
	formation('Weak', 'Weak', 'Tight end opposite the flanker'),
	formation('Ace', 'Ace', 'Single back, two tight ends'),
	formation('Jumbo', 'Jumbo', 'Extra tight ends or linemen in heavy personnel'),
	formation('GL', 'Goal Line', 'Short yardage / goal line personnel'),

	// Modifiers (11)
	modifier('Right', 'Right', 'Strength to the right'),
	modifier('Left', 'Left', 'Strength to the left'),
	modifier('Str', 'Strong', 'Align to the strong side'),
	modifier('Wk', 'Weak', 'Align to the weak side'),
	modifier('IForm', 'I Formation', 'I-formation backfield alignment'),
	modifier('Split', 'Split', 'Backs split to opposite sides'),
	modifier('Offset', 'Offset', 'Back offset to one side'),
	modifier('Open', 'Open', 'Open side alignment'),
	modifier('Tight', 'Tight', 'Tight formation, compressed splits'),
	modifier('Wide', 'Wide', 'Wide receiver splits extended'),
	modifier('Nasty', 'Nasty', 'Unbalanced or unconventional alignment'),

	// Route Concepts (12)
	route('Mesh', 'Mesh', 'Crossing routes from opposite sides'),
	route('Shallow', 'Shallow', 'Shallow drag route across the formation'),
	route('Smash', 'Smash', 'Corner-hitch combination'),
	route('Flood', 'Flood', 'Three-level stretch to one side'),
	route('Verts', 'Verticals', 'Four vertical routes'),
	route('Levels', 'Levels', 'Layered routes at different depths'),
	route('YX', 'Y-Cross', 'Tight end deep cross with underneath options'),
	route('Dagger', 'Dagger', 'Post-dig combination behind linebackers'),
	route('Stick', 'Stick', 'Quick stick route with flat and corner options'),
	route('Snag', 'Snag', 'Spot-corner-flat triangle concept'),
	route('Drive', 'Drive', 'Quick in routes at different levels'),
	route('SF', 'Slant-Flat', 'Slant and flat combination'),

	// Run Schemes / Blocking (11)
	blocking('Power', 'Power', 'Power run with pulling guard'),
	blocking('Counter', 'Counter', 'Counter run with two pulling linemen'),
	blocking('Zone', 'Zone', 'Inside zone run scheme'),
	blocking('Outside', 'Outside Zone', 'Outside zone / stretch run'),
	blocking('Iso', 'Iso', 'Isolation run with lead fullback'),
	blocking('Trap', 'Trap', 'Trap block with pulling guard'),
	blocking('Dive', 'Dive', 'Quick dive up the middle'),
	blocking('Draw', 'Draw', 'Delayed handoff after pass fake'),
	blocking('Sweep', 'Sweep', 'Wide sweep around the edge'),
	blocking('Duo', 'Duo', 'Double team at the point of attack'),
	blocking('Wham', 'Wham', 'Wham block by tight end or fullback'),

	// Motions (3)
	motion('Jet', 'Jet', 'Full-speed motion across the formation'),
	motion('Orbit', 'Orbit', 'Orbit motion behind the quarterback'),
	motion('Fly', 'Fly', 'Fly motion toward the sideline'),

	// Protections (2)
	protection('Max', 'Max Protect', 'Maximum protection with extra blockers'),
	protection('Slide', 'Slide', 'Slide protection to one side'),
]

// ---------------------------------------------------------------------------
// Air Raid — 19 entries
// ---------------------------------------------------------------------------

const AIR_RAID_VOCABULARY: PresetVocabularyEntry[] = [
	// Formations (5)
	formation('Trips', 'Trips', 'Three receivers to one side'),
	formation('Doubles', 'Doubles', 'Two receivers to each side'),
	formation('Empty', 'Empty', 'No running back in the backfield'),
	formation('Ace', 'Ace', 'Single back, balanced receivers'),
	formation('Bunch', 'Bunch', 'Three receivers grouped tightly'),

	// Modifiers (2)
	modifier('Right', 'Right', 'Strength to the right'),
	modifier('Left', 'Left', 'Strength to the left'),

	// Route Concepts (7)
	route('Mesh', 'Mesh', 'Shallow crossing routes from opposite sides'),
	route('YX', 'Y-Cross', 'Deep cross by slot or tight end'),
	route('Verts', 'Verticals', 'Four vertical routes'),
	route('Shallow', 'Shallow', 'Shallow drag across the formation'),
	route('Stick', 'Stick', 'Quick stick with flat and corner'),
	route('Smash', 'Smash', 'Corner-hitch combination'),
	route('Drive', 'Drive', 'Quick in routes at different levels'),

	// Run Schemes (2)
	blocking('Zone', 'Zone', 'Inside zone run'),
	blocking('Draw', 'Draw', 'Delayed handoff draw play'),

	// Motions (1)
	motion('Jet', 'Jet', 'Full-speed jet motion'),

	// Protections (2)
	protection('60', '60 Protection', 'Six-man slide protection'),
	protection('70', '70 Protection', 'Seven-man maximum protection'),
]

// ---------------------------------------------------------------------------
// Spread — 19 entries
// ---------------------------------------------------------------------------

const SPREAD_VOCABULARY: PresetVocabularyEntry[] = [
	// Formations (6)
	formation('Spread', 'Spread', 'Four wide receivers spread across the field'),
	formation('Trips', 'Trips', 'Three receivers to one side'),
	formation('Doubles', 'Doubles', 'Two receivers to each side'),
	formation('Bunch', 'Bunch', 'Three receivers grouped tightly'),
	formation('Empty', 'Empty', 'No running back in the backfield'),
	formation('Pistol', 'Pistol', 'Quarterback in pistol with deep back'),

	// Modifiers (4)
	modifier('Right', 'Right', 'Strength to the right'),
	modifier('Left', 'Left', 'Strength to the left'),
	modifier('Gun', 'Shotgun', 'Shotgun snap'),
	modifier('Pstl', 'Pistol', 'Pistol alignment'),

	// Route Concepts (5)
	route('Mesh', 'Mesh', 'Crossing routes from opposite sides'),
	route('RPO', 'RPO', 'Run-pass option with read key'),
	route('Screen', 'Screen', 'Quick screen pass behind the line'),
	route('Bubble', 'Bubble', 'Bubble screen to a wide receiver'),
	route('Verts', 'Verticals', 'Four vertical routes'),

	// Run Schemes (4) — note: Spread has no motion or protection entries
	blocking('Zone', 'Zone', 'Inside zone run scheme'),
	blocking('Power', 'Power', 'Power run with pulling guard'),
	blocking('Counter', 'Counter', 'Counter run with two pulling linemen'),
	blocking('Sweep', 'Sweep', 'Wide sweep to the outside'),
]

// ---------------------------------------------------------------------------
// Presets Array
// ---------------------------------------------------------------------------

export const FOOTBALL_PRESETS: PresetSystem[] = [
	{
		id: 'pro_style',
		name: 'Pro Style',
		description: 'Traditional pro-style offense with balanced run and pass. Includes two-back formations, play-action concepts, and a full suite of run schemes.',
		vocabulary: PRO_STYLE_VOCABULARY,
	},
	{
		id: 'air_raid',
		name: 'Air Raid',
		description: 'Pass-heavy spread offense emphasizing quick reads and high-percentage throws. Focuses on four and five wide receiver sets.',
		vocabulary: AIR_RAID_VOCABULARY,
	},
	{
		id: 'spread',
		name: 'Spread',
		description: 'Spread offense using space and tempo to create mismatches. Combines zone-read running with quick passing concepts.',
		vocabulary: SPREAD_VOCABULARY,
	},
	{
		id: 'custom',
		name: 'Custom',
		description: 'Start with a blank slate. Build your vocabulary from scratch as you create formations and concepts.',
		vocabulary: [],
	},
]

// ---------------------------------------------------------------------------
// Lookup utilities
// ---------------------------------------------------------------------------

/**
 * Returns the entity type for a given vocabulary category.
 * Centralizes the category-to-entity-type mapping.
 */
export function entityTypeForCategory(category: VocabularyCategory): VocabularyEntityType {
	switch (category) {
		case 'formation':
			return 'formation'
		case 'modifier':
			return 'modifier'
		case 'route':
		case 'blocking':
		case 'motion':
		case 'protection':
			return 'concept'
	}
}

/**
 * Finds a preset by its ID.
 */
export function findPreset(id: string): PresetSystem | undefined {
	return FOOTBALL_PRESETS.find(p => p.id === id)
}
